import { skipToken, useQuery } from "@apollo/client/react";
import type { DeepPartial } from "@apollo/client/utilities";
import Image from "next/image";
import type { ReactNode } from "react";
import { useContext } from "react";
import type { FallbackProps } from "react-error-boundary";
import { ErrorBoundary } from "react-error-boundary";

import DiscoverStatFooter from "@/components/app/discover/discover-stat-footer";
import { FollowUserListsButton } from "@/components/app/lists/follow-user-lists-button";
import DesoAvatar from "@/components/shared/deso-avatar";
import { HolderBadge } from "@/components/shared/holder-badge";
import { IsLoggedIn } from "@/components/shared/is-logged-in";
import { TruncatedContent } from "@/components/shared/truncated-content";
import { UserFollowCounts } from "@/components/shared/user-follow-counts";
import type { FilterRecord } from "@/contexts/filters-sort-context";
import { UserContext } from "@/contexts/user-context";
import {
  type CreatorStat,
  CreatorStatAccountDataDocument,
  type TokenMetadatum,
} from "@/graphql/codegen/graphql";
import { useIdleReady } from "@/hooks/use-idle-ready";
import { cn } from "@/utils/lib";
import { getMarkdownBody } from "@/utils/post-utils";
import { isCreatorStatType } from "@/utils/types";
import { getUserDisplayName, getUserName } from "@/utils/usernames";

import { UserDisplayNameWithStatus } from "../../shared/user-display-name-with-status";
import type { DiscoverFilterFields } from "../discover-sort-constants";
import { ProfileActionsDropdown } from "../profile/profile-actions-dropdown";
import UserConnectedSocialsList from "../user-connected-socials-list";

function DiscoverCreatorItemFallback({ resetErrorBoundary }: FallbackProps) {
  return (
    <div
      className="
        p-4
        md:p-6
      "
    >
      <div className="text-center text-red-500">
        An error occurred while loading this creator.
        <button onClick={resetErrorBoundary} className="ml-2 underline">
          Retry
        </button>
      </div>
    </div>
  );
}

export const DiscoverCreatorItemErrorBoundary = ({
  children,
}: {
  children: ReactNode;
}) => (
  <ErrorBoundary FallbackComponent={DiscoverCreatorItemFallback}>
    {children}
  </ErrorBoundary>
);

export const DiscoverCreatorItem = ({
  user,
  isCompact,
  extraContent,
  findFilterByName,
  hasBorder = true,
  isRounded = true,
  customPrefix,
  filterBlockedAccount,
}: {
  user: DeepPartial<CreatorStat | TokenMetadatum>;
  isCompact?: boolean;
  extraContent?: ReactNode;
  findFilterByName?: (name: DiscoverFilterFields) => FilterRecord | undefined;
  hasBorder?: boolean;
  isRounded?: boolean;
  customPrefix?: string;
  filterBlockedAccount?: (publicKey: string) => void;
}) => {
  const { currentUserPublicKey } = useContext(UserContext);

  const {
    publicKeyBase58Check,
    username,
    totalFollowers,
    displayName,
    description,
    coinCategory,
  } = user;

  const finalPublicKey = publicKeyBase58Check ?? "";
  const isMyAccount = currentUserPublicKey === finalPublicKey;
  const idleCreatorStatReady = useIdleReady(Boolean(finalPublicKey), {
    timeout: 1500,
  });

  const totalFollowing = isCreatorStatType(user)
    ? (user?.totalFollowing ?? 0)
    : 0;

  const {
    data: creatorStatAccountData,
    refetch: refetchCreatorStatAccountData,
  } = useQuery(
    CreatorStatAccountDataDocument,
    finalPublicKey && idleCreatorStatReady
      ? {
          variables: {
            publicKey: finalPublicKey,
            tokenBalancesFilter: {
              creator: {
                publicKey: {
                  equalTo: currentUserPublicKey,
                },
              },
            },
          },
        }
      : skipToken,
  );

  const account = creatorStatAccountData?.accountByPublicKey;

  const extraData = {
    DisplayName: displayName ?? "",
    FeaturedImageURL: account?.extraData?.FeaturedImageURL ?? "",
    TwitterURL: account?.extraData?.TwitterURL ?? "",
    DiscordURL: account?.extraData?.DiscordURL ?? "",
    InstagramURL: account?.extraData?.InstagramURL ?? "",
    TwitchURL: account?.extraData?.TwitchURL ?? "",
    WebsiteURL: account?.extraData?.WebsiteURL ?? "",
    NFTProfilePicturePostHashHex:
      account?.extraData?.NFTProfilePicturePostHashHex,
    NFTProfilePictureUrl: account?.extraData?.NFTProfilePictureUrl,
  };

  const featuredImage = extraData.FeaturedImageURL || "/assets/empty-39.png";
  const userLabel = getUserDisplayName({
    extraData,
    publicKey: finalPublicKey,
    username: username ?? "",
    truncateLength: 40,
  });

  const creatorHoldingCurrentUserToken = Boolean(
    account?.tokenBalances?.nodes?.[0]?.balanceNanos,
  );

  if (!finalPublicKey) return;

  return (
    <div
      className={cn(
        `
          relative rounded-2xl bg-background transition-all
          hover:bg-accent
        `,
        {
          "rounded-t-none": !isRounded,
        },
      )}
    >
      <div
        className={cn("rounded-t-2xl", {
          "rounded-t-2xl border border-border": hasBorder,
          "rounded-t-none": !isRounded,
        })}
      >
        <div
          className={cn("relative min-h-[120px] w-full overflow-hidden", {
            "rounded-t-2xl": isRounded,
            "rounded-t-none": !isRounded,
          })}
          style={{
            WebkitMaskImage:
              "linear-gradient(180deg, #000000FF 0%, #00000000 100%)",
          }}
        >
          <Image
            src={featuredImage}
            width={700}
            height={120}
            quality={90}
            alt="Cover Image"
            className={cn("min-w-full cursor-pointer object-cover", {
              "rounded-t-2xl": isRounded,
              "rounded-t-none": !isRounded,
            })}
            style={{ width: "100%", height: 120 }}
            priority
          />
        </div>
        <div
          className="
            -mt-12 flex flex-col items-start gap-4 p-4
            md:p-6
          "
        >
          <div className="w-full">
            <div className="flex w-full flex-row items-end justify-between">
              <div className="flex flex-row items-center gap-3">
                {user && (
                  <DesoAvatar
                    account={{ username, publicKey: finalPublicKey, extraData }}
                    size={56}
                  />
                )}
                <div className="relative flex flex-col gap-1">
                  <div className="flex flex-row items-center">
                    <UserDisplayNameWithStatus
                      whaleSize={20}
                      className="font-sans"
                      {...{
                        username: username ?? "",
                        publicKey: finalPublicKey,
                        extraData,
                      }}
                    />
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted">
                    {getUserName({
                      account: {
                        username,
                        publicKey: account?.publicKey,
                      },
                      customPrefix: customPrefix || (coinCategory ? "$" : "@"),
                    })}
                    <div className="flex items-center gap-2">
                      <UserConnectedSocialsList
                        publicKey={finalPublicKey}
                        isMyAccount={isMyAccount}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <IsLoggedIn>
                  <div
                    className="
                      hidden
                      sm:mr-2 sm:block
                    "
                  >
                    <FollowUserListsButton
                      userLabel={userLabel}
                      userPublicKey={finalPublicKey}
                      variant="inline"
                    />
                  </div>

                  <ProfileActionsDropdown
                    account={{
                      publicKey: finalPublicKey,
                      username: username ?? "",
                      extraData,
                    }}
                    onBlock={(blocked: boolean) => {
                      if (blocked) {
                        filterBlockedAccount?.(finalPublicKey);
                      }
                      void refetchCreatorStatAccountData();
                    }}
                  />
                </IsLoggedIn>
              </div>
            </div>
            <div
              className="
                mt-4 mb-2 flex flex-col items-start gap-2 text-sm/6
                text-foreground
                md:pr-12 md:leading-6
              "
            >
              <UserFollowCounts
                {...{
                  followersCount: Number(totalFollowers ?? "0"),
                  followingCount: Number(totalFollowing ?? "0"),
                  publicKey: publicKeyBase58Check ?? "",
                  username: username ?? "",
                }}
              />
              {creatorHoldingCurrentUserToken && <HolderBadge />}
              {!!description && (
                <TruncatedContent maxHeight={100}>
                  {getMarkdownBody(description)}
                </TruncatedContent>
              )}
            </div>

            <div className="mt-4">
              <DiscoverStatFooter
                user={user}
                isCompact={isCompact}
                findFilterByName={findFilterByName}
              />
            </div>
            {extraContent}
          </div>
        </div>
      </div>
    </div>
  );
};
